// Libs
import classNames from 'classnames/bind';
import { Box, IconButton, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
// Components, Layouts, Pages
// Others
// Styles, images, icons
import styles from './BaseTableOption.module.scss';
import { icons } from '~/assets';

type Props = {
  onView?: () => void;
  onEdit?: () => void;
  onCall?: () => void;
  onSMS?: () => void;
  onMail?: () => void;
  onConvertToCustomer?: () => void;
  onDelete?: () => void;
  onMakeStatus?: () => void;
  onMessage?: () => void;
  onMarkAsUnpaid?: () => void;
  onMarkAsPaid?: () => void;
  onChangeStatus?: () => void;
  onSendEstimate?: () => void;
  onSendToQuickBooks?: () => void;
  onConvertToJob?: () => void;
};

const cx = classNames.bind(styles);

const BaseTableOption = (props: Props) => {
  //#region Destructuring Props
  const {
    onView,
    onEdit,
    onCall,
    onSMS,
    onMail,
    onConvertToCustomer,
    onDelete,
    onMakeStatus,
    onMessage,
    onMarkAsUnpaid,
    onMarkAsPaid,
    onChangeStatus,
    onSendEstimate,
    onSendToQuickBooks,
    onConvertToJob,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleShowMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleView = () => {
    onView && onView();
    handleCloseMenu();
  };

  const handleEdit = () => {
    onEdit && onEdit();
    handleCloseMenu();
  };

  const handleCall = () => {
    onCall && onCall();
    handleCloseMenu();
  };

  const handleSMS = () => {
    onSMS && onSMS();
    handleCloseMenu();
  };

  const handleMail = () => {
    onMail && onMail();
    handleCloseMenu();
  };

  const handleConvertToCustomer = () => {
    onConvertToCustomer && onConvertToCustomer();
    handleCloseMenu();
  };

  const handleDelete = () => {
    onDelete && onDelete();
    handleCloseMenu();
  };

  const handleMakeStatus = () => {
    onMakeStatus && onMakeStatus();
    handleCloseMenu();
  };

  const handleMessage = () => {
    onMessage && onMessage();
    handleCloseMenu();
  };

  const handleMarkAsUnpaid = () => {
    onMarkAsUnpaid && onMarkAsUnpaid();
    handleCloseMenu();
  };

  const handleMarkAsPaid = () => {
    onMarkAsPaid && onMarkAsPaid();
    handleCloseMenu();
  };

  const handleChangeStatus = () => {
    onChangeStatus && onChangeStatus();
    handleCloseMenu();
  };

  const handleSendEstimate = () => {
    onSendEstimate && onSendEstimate();
    handleCloseMenu();
  };

  const handleSendToQuickBooks = () => {
    onSendToQuickBooks && onSendToQuickBooks();
    handleCloseMenu();
  };

  const handleConvertToJob = () => {
    onConvertToJob && onConvertToJob();
    handleCloseMenu();
  };
  //#endregion Handle Function

  return (
    <Box id='baseTableOptionComponent' className={cx('container')}>
      <IconButton
        size='small'
        onClick={(event) => {
          event.stopPropagation();
          handleShowMenu(event);
        }}
      >
        <img src={icons.tableIconMenuDots} alt={t('common_alt_img')} className={cx('moreActionIcon')} />
      </IconButton>

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();
          handleCloseMenu();
        }}
        slotProps={{
          paper: {
            className: cx('paper'),
          },
        }}
        MenuListProps={{ className: cx('menuList') }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoFocus={false}
      >
        {onView && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleView();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img src={icons.tableIconActionView} alt={t('common_alt_img')} className={cx('itemIconImg')} />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_view')}</Typography>}
            />
          </MenuItem>
        )}

        {onEdit && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleEdit();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img src={icons.tableIconActionEdit} alt={t('common_alt_img')} className={cx('itemIconImg')} />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_edit')}</Typography>}
            />
          </MenuItem>
        )}

        {onMessage && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleMessage();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionMessage}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_message')}</Typography>}
            />
          </MenuItem>
        )}

        {onCall && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleCall();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img src={icons.tableIconActionCall} alt={t('common_alt_img')} className={cx('itemIconImg')} />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_call')}</Typography>}
            />
          </MenuItem>
        )}

        {onSMS && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleSMS();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img src={icons.tableIconActionSMS} alt={t('common_alt_img')} className={cx('itemIconImg')} />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_sms')}</Typography>}
            />
          </MenuItem>
        )}

        {onMail && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleMail();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img src={icons.tableIconActionMail} alt={t('common_alt_img')} className={cx('itemIconImg')} />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_mail')}</Typography>}
            />
          </MenuItem>
        )}

        {onConvertToCustomer && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleConvertToCustomer();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionConvert}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={
                <Typography className={cx('itemText')}>{t('table_action_convert_to_customer')}</Typography>
              }
            />
          </MenuItem>
        )}

        {onMarkAsUnpaid && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleMarkAsUnpaid();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionMakeAsUnpaid}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_mark_as_unpaid')}</Typography>}
            />
          </MenuItem>
        )}

        {onMarkAsPaid && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleMarkAsPaid();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionMakeAsPaid}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_mark_as_paid')}</Typography>}
            />
          </MenuItem>
        )}

        {onMakeStatus && (
          <MenuItem
            className={cx('itemButton', 'makeStatus')}
            onClick={(event) => {
              event.stopPropagation();
              handleMakeStatus();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionMakeStatus}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={
                <Typography className={cx('itemText', 'makeStatus')}>
                  {t('table_action_make_status')}
                </Typography>
              }
            />
          </MenuItem>
        )}

        {onSendEstimate && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleSendEstimate();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionSendEstimate}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_send_estimate')}</Typography>}
            />
          </MenuItem>
        )}

        {onSendToQuickBooks && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleSendToQuickBooks();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionSendToQuickBooks}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={
                <Typography className={cx('itemText')}>{t('table_action_send_to_quick_books')}</Typography>
              }
            />
          </MenuItem>
        )}

        {onChangeStatus && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleChangeStatus();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionChangeStatus}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_change_status')}</Typography>}
            />
          </MenuItem>
        )}

        {onConvertToJob && (
          <MenuItem
            className={cx('itemButton')}
            onClick={(event) => {
              event.stopPropagation();
              handleConvertToJob();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionChangeStatus}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={<Typography className={cx('itemText')}>{t('table_action_convert_to_job')}</Typography>}
            />
          </MenuItem>
        )}

        {onDelete && (
          <MenuItem
            className={cx('itemButton', 'delete')}
            onClick={(event) => {
              event.stopPropagation();
              handleDelete();
            }}
          >
            <Box className={cx('itemIcon')}>
              <img
                src={icons.tableIconActionDelete}
                alt={t('common_alt_img')}
                className={cx('itemIconImg')}
              />
            </Box>

            <ListItemText
              primary={
                <Typography className={cx('itemText', 'delete')}>{t('table_action_delete')}</Typography>
              }
            />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};

export default BaseTableOption;
